// src/app/modules/demandes/BulkUsersFieldsModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import "../../RessourcesStyles/UsersListModal.css";
import "../../RessourcesStyles/CollaboratorSelection.css";

const makeRow = (u) => ({
    targetUserId: u?.id,
    mode: "",
    period: "",
    niveau: "",
    certifiante: false,
    priorite: 2,
    objectifs: "",
    objectifs2: "",
    objectifs3: "",
});

const norm = (v) => String(v ?? "").trim().toLowerCase();

function userLabel(u) {
    const first = u?.firstName || u?.prenom || "";
    const last = u?.lastName || u?.nom || "";
    return `${first} ${last}`.trim() || u?.username || u?.login || `User#${u?.id ?? ""}`;
}

function getUserDetail(u) {
    const parts = [];
    const mat = u?.matricule || u?.employeeCode;
    if (mat) parts.push(`Mat: ${mat}`);
    const email = u?.email || u?.mail;
    if (email) parts.push(email);
    const entity = u?.entityName || u?.entiteNom || u?.etablissementNom || u?.companyName;
    if (entity) parts.push(entity);
    const poste = u?.jobName || u?.jobTitle || u?.poste || u?.jobText;
    if (poste) parts.push(poste);
    return parts.join(" · ") || "—";
}

/* ── SVG Icons ── */
const SettingsIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
);
const EmptyIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
        <line x1="18" y1="8" x2="23" y2="13" /><line x1="23" y1="8" x2="18" y2="13" />
    </svg>
);
const AlertIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
);


const PERIOD_OPTIONS = ["Q1", "Q2", "Q3", "Q4"];
const NIVEAU_OPTIONS = ["Débutant", "Intermédiaire", "Avancé"];
const MODE_OPTIONS = ["Distanciel", "Présentiel", "Hybride"];

export default function BulkUsersFieldsModal({ open, onClose, users = [], onSubmit, title }) {
    const [rows, setRows] = useState([]);
    const [applyAll, setApplyAll] = useState(makeRow({ id: -1 }));
    const [filterUser, setFilterUser] = useState("");
    const [showApplyAll, setShowApplyAll] = useState(true);

    useEffect(() => {
        if (!open) return;
        setRows((users || []).map(makeRow));
        setApplyAll(makeRow({ id: -1 }));
        setFilterUser("");
        setShowApplyAll(true);
    }, [open, users]);

    const update = (idx, key, val) => {
        setRows((prev) => {
            const next = [...prev];
            next[idx] = { ...next[idx], [key]: val };
            return next;
        });
    };

    const applyToAll = () => {
        setRows((prev) =>
            prev.map((r) => ({
                ...r,
                mode: applyAll.mode,
                period: applyAll.period,
                niveau: applyAll.niveau,
                certifiante: !!applyAll.certifiante,
                priorite: Number(applyAll.priorite || 2),
                objectifs: applyAll.objectifs,
                objectifs2: applyAll.objectifs2,
                objectifs3: applyAll.objectifs3,
            }))
        );
    };

    const invalidCount = useMemo(() => {
        return (rows || []).filter((r) => {
            const p = Number(r?.priorite);
            return !r?.targetUserId || ![1, 2, 3].includes(p);
        }).length;
    }, [rows]);

    const filteredIndexes = useMemo(() => {
        const q = norm(filterUser);
        if (!q) return users.map((_, idx) => idx);

        const idxs = [];
        (users || []).forEach((u, idx) => {
            const name = norm(userLabel(u));
            const matricule = norm(u?.matricule);
            const email = norm(u?.email || u?.mail);
            const entity = norm(u?.entityName || u?.entiteNom || u?.etablissementNom || u?.companyName);
            if (name.includes(q) || matricule.includes(q) || email.includes(q) || entity.includes(q)) idxs.push(idx);
        });
        return idxs;
    }, [users, filterUser]);

    const submit = () => {
        if (invalidCount > 0) return;
        onSubmit?.(rows);
    };

    if (!open) return null;

    return (
        <div className="collaborator-selection-overlay" onMouseDown={onClose}>
            <div className="collaborator-settings-card" onMouseDown={(e) => e.stopPropagation()}>

                {/* ── Header ── */}
                <div className="collaborator-settings-header">
                    <div className="collaborator-settings-title-block">
                        <div className="collaborator-settings-title">
                            {title || "Paramétrage par collaborateur"}
                        </div>
                        <div className="collaborator-settings-subtitle">
                            <span>{users?.length || 0} collaborateur(s)</span>
                            {invalidCount > 0 ? (
                                <span className="collaborator-settings-badge collaborator-settings-badge--warn">
                                    {invalidCount} ligne(s) invalide(s)
                                </span>
                            ) : (
                                users?.length > 0 && (
                                    <span className="collaborator-settings-badge collaborator-settings-badge--ok">
                                        Prêt
                                    </span>
                                )
                            )}
                        </div>
                    </div>
                    <button className="collaborator-selection-close" type="button" onClick={onClose} aria-label="Fermer">
                        ✕
                    </button>
                </div>

                {/* ── Body ── */}
                <div className="collaborator-settings-body">

                    {/* Search + Toggle */}
                    <div className="collaborator-selection-filters" style={{ gridTemplateColumns: "1fr 1fr auto", marginBottom: 16 }}>
                        <div className="collaborator-selection-filter-field" style={{ gridColumn: "1 / span 2" }}>
                            <label>Filtrer les collaborateurs</label>
                            <input
                                value={filterUser}
                                onChange={(e) => setFilterUser(e.target.value)}
                                placeholder="Rechercher par nom, matricule, email, entité..."
                            />
                        </div>
                        <div style={{ display: "flex", alignItems: "flex-end" }}>
                            <button
                                className="cs-btn cs-btn--secondary"
                                type="button"
                                onClick={() => setShowApplyAll((v) => !v)}
                            >
                                <SettingsIcon />
                                {showApplyAll ? "Masquer" : "Appliquer à tous"}
                            </button>
                        </div>
                    </div>

                    {/* ── Apply-All Section ── */}
                    {showApplyAll && (
                        <div className="collaborator-settings-section">
                            <div className="collaborator-settings-section-header">
                                <div className="collaborator-settings-section-title">Paramètres globaux</div>
                                <div className="collaborator-settings-section-line" />
                                <button
                                    className="cs-btn cs-btn--primary"
                                    type="button"
                                    onClick={applyToAll}
                                    style={{ padding: "7px 14px" }}
                                >
                                    Appliquer à tous
                                </button>
                            </div>

                            <div className="collaborator-settings-apply-all">
                                <div className="collaborator-settings-grid">
                                    <div className="collaborator-settings-field">
                                        <label>Période</label>
                                        <select
                                            value={applyAll.period || ""}
                                            onChange={(e) => setApplyAll((s) => ({ ...s, period: e.target.value }))}
                                        >
                                            <option value="">—</option>
                                            {PERIOD_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                                        </select>
                                    </div>

                                    <div className="collaborator-settings-field">
                                        <label>Niveau</label>
                                        <select
                                            value={applyAll.niveau || ""}
                                            onChange={(e) => setApplyAll((s) => ({ ...s, niveau: e.target.value }))}
                                        >
                                            <option value="">—</option>
                                            {NIVEAU_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
                                        </select>
                                    </div>

                                    <div className="collaborator-settings-field">
                                        <label>Mode</label>
                                        <select
                                            value={applyAll.mode || ""}
                                            onChange={(e) => setApplyAll((s) => ({ ...s, mode: e.target.value }))}
                                        >
                                            <option value="">—</option>
                                            {MODE_OPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
                                        </select>
                                    </div>
                                </div>

                                <div className="collaborator-settings-grid" style={{ marginTop: 12 }}>
                                    <div className="collaborator-settings-field">
                                        <label>Priorité</label>
                                        <select
                                            value={applyAll.priorite}
                                            onChange={(e) => setApplyAll((s) => ({ ...s, priorite: Number(e.target.value) }))}
                                        >
                                            <option value={1}>1 – Haute</option>
                                            <option value={2}>2 – Moyenne</option>
                                            <option value={3}>3 – Basse</option>
                                        </select>
                                    </div>

                                    <div className="collaborator-settings-field">
                                        <label>Certifiante</label>
                                        <div className="cs-checkbox-row">
                                            <input
                                                type="checkbox"
                                                checked={!!applyAll.certifiante}
                                                onChange={(e) => setApplyAll((s) => ({ ...s, certifiante: e.target.checked }))}
                                                style={{ width: 17, height: 17, accentColor: "#204385", cursor: "pointer" }}
                                            />
                                            <span className="cs-checkbox-label">Oui / Non</span>
                                        </div>
                                    </div>

                                    <div />
                                </div>

                                <div className="collaborator-settings-grid" style={{ marginTop: 12 }}>
                                    <div className="collaborator-settings-field">
                                        <label>Objectifs 1</label>
                                        <input value={applyAll.objectifs} onChange={(e) => setApplyAll((s) => ({ ...s, objectifs: e.target.value }))} placeholder="Objectif principal..." />
                                    </div>
                                    <div className="collaborator-settings-field">
                                        <label>Objectifs 2</label>
                                        <input value={applyAll.objectifs2} onChange={(e) => setApplyAll((s) => ({ ...s, objectifs2: e.target.value }))} placeholder="Objectif secondaire..." />
                                    </div>
                                    <div className="collaborator-settings-field">
                                        <label>Objectifs 3</label>
                                        <input value={applyAll.objectifs3} onChange={(e) => setApplyAll((s) => ({ ...s, objectifs3: e.target.value }))} placeholder="Objectif additionnel..." />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Per-User Table Section ── */}
                    <div className="collaborator-settings-section">
                        <div className="collaborator-settings-section-header">
                            <div className="collaborator-settings-section-title">Paramètres individuels</div>
                            <div className="collaborator-settings-section-line" />
                            <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 600, whiteSpace: "nowrap" }}>
                                {filteredIndexes.length} / {users?.length || 0} collaborateur(s)
                            </span>
                        </div>

                        <div className="collaborator-settings-table-wrap">
                            <table className="collaborator-settings-table">
                                <thead>
                                <tr>
                                    <th style={{ minWidth: 200 }}>Collaborateur</th>
                                    <th style={{ minWidth: 120 }}>Période</th>
                                    <th style={{ minWidth: 140 }}>Niveau</th>
                                    <th style={{ minWidth: 120 }}>Mode</th>
                                    <th style={{ width: 80 }} className="cs-center">Certif.</th>
                                    <th style={{ width: 80 }}>Priorité</th>
                                    <th style={{ minWidth: 180 }}>Objectifs 1</th>
                                    <th style={{ minWidth: 180 }}>Objectifs 2</th>
                                    <th style={{ minWidth: 180 }}>Objectifs 3</th>
                                </tr>
                                </thead>

                                <tbody>
                                {filteredIndexes.length === 0 ? (
                                    <tr>
                                        <td colSpan={9}>
                                            <div className="collaborator-selection-empty">
                                                <div className="collaborator-selection-empty-icon"><EmptyIcon /></div>
                                                <div className="collaborator-selection-empty-text">
                                                    {filterUser
                                                        ? "Aucun collaborateur ne correspond au filtre"
                                                        : "Aucun collaborateur sélectionné"}
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    filteredIndexes.map((idx) => {
                                        const u = users[idx];
                                        const r = rows[idx] || makeRow(u);
                                        const invalid = !r?.targetUserId || ![1, 2, 3].includes(Number(r?.priorite));

                                        return (
                                            <tr key={u?.id ?? `${userLabel(u)}-${idx}`} className={invalid ? "collaborator-settings-row--invalid" : ""}>
                                                <td>
                                                    <div className="collaborator-settings-user-cell">
                                                        <div className="collaborator-settings-user-name">{userLabel(u)}</div>
                                                        {/*<div className="collaborator-settings-user-detail">{getUserDetail(u)}</div>*/}
                                                    </div>
                                                </td>

                                                <td>
                                                    <select value={r.period || ""} onChange={(e) => update(idx, "period", e.target.value)}>
                                                        <option value="">—</option>
                                                        {PERIOD_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                                                    </select>
                                                </td>

                                                <td>
                                                    <select value={r.niveau || ""} onChange={(e) => update(idx, "niveau", e.target.value)}>
                                                        <option value="">—</option>
                                                        {NIVEAU_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
                                                    </select>
                                                </td>

                                                <td>
                                                    <select value={r.mode || ""} onChange={(e) => update(idx, "mode", e.target.value)}>
                                                        <option value="">—</option>
                                                        {MODE_OPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
                                                    </select>
                                                </td>

                                                <td className="cs-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={!!r.certifiante}
                                                        onChange={(e) => update(idx, "certifiante", e.target.checked)}
                                                    />
                                                </td>

                                                <td>
                                                    <select value={r.priorite ?? 2} onChange={(e) => update(idx, "priorite", Number(e.target.value))}>
                                                        <option value={1}>1</option>
                                                        <option value={2}>2</option>
                                                        <option value={3}>3</option>
                                                    </select>
                                                </td>

                                                <td>
                                                    <textarea rows={2} value={r.objectifs || ""} onChange={(e) => update(idx, "objectifs", e.target.value)} placeholder="..." />
                                                </td>
                                                <td>
                                                    <textarea rows={2} value={r.objectifs2 || ""} onChange={(e) => update(idx, "objectifs2", e.target.value)} placeholder="..." />
                                                </td>
                                                <td>
                                                    <textarea rows={2} value={r.objectifs3 || ""} onChange={(e) => update(idx, "objectifs3", e.target.value)} placeholder="..." />
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Validation error */}
                    {invalidCount > 0 && (
                        <div className="collaborator-settings-error">
                            <AlertIcon />
                            Certaines lignes sont invalides (Priorité doit être 1, 2 ou 3). Corrigez avant d'envoyer.
                        </div>
                    )}
                </div>

                {/* ── Footer ── */}
                <div className="collaborator-settings-actions">
                    <div className="collaborator-settings-actions-left">
                        <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 600 }}>
                            {users?.length || 0} collaborateur(s) · {invalidCount === 0 ? "Tous valides" : `${invalidCount} invalide(s)`}
                        </span>
                    </div>
                    <div className="collaborator-settings-actions-right">
                        <button className="cs-btn cs-btn--secondary" type="button" onClick={onClose}>
                            Annuler
                        </button>
                        <button className="cs-btn cs-btn--primary" type="button" onClick={submit} disabled={users.length === 0 || invalidCount > 0}>
                            Envoyer les demandes
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
